import {
  ConsolidateTechnologiesRequest,
  TechnologyCreateRequest,
  TechnologyDictionaryModel,
  TechnologyUpdateRequest,
} from "../../models/technology.model";
import { BackendHttpService } from "../backendHttpService";
import { technologyDictionaryService } from "../dictionaries";

/**
 * Technology Dictionary Admin Service
 *
 * Administrative endpoints for technology dictionary management including:
 * - Technology dictionary CRUD operations
 * - Consolidation operations for technologies
 *
 * These endpoints are typically restricted to administrators.
 *
 * TODO: Backend implementation is missing for create, update, and delete operations.
 * The following endpoints need to be implemented in the backend:
 * - POST /Dictionary/technologies - Create new technology
 * - PUT /Dictionary/technologies/{id} - Update technology
 * - DELETE /Dictionary/technologies/{id} - Delete technology
 */
export class TechnologyDictionaryAdminService extends BackendHttpService {
  constructor() {
    super("dictionary");
  }

  // Clear cache when dictionary changes
  private clearCache(): void {
    // Clear cache in the dictionary service
    technologyDictionaryService.clearCache();
  }

  // ===== TECHNOLOGY DICTIONARY METHODS (ADMIN ONLY) =====

  /**
   * Get all technologies from dictionary - delegated to dictionary service
   */
  async getTechnologiesDictionary(): Promise<TechnologyDictionaryModel[]> {
    return technologyDictionaryService.getTechnologiesDictionary();
  }

  /**
   * Create a new technology in dictionary
   * POST /Dictionary/technologies
   *
   * TODO: Backend endpoint not implemented yet
   */
  async createTechnology(
    request: TechnologyCreateRequest
  ): Promise<TechnologyDictionaryModel> {
    try {
      // TODO: This will fail until backend endpoint is implemented
      const technology = await this.post<
        TechnologyCreateRequest,
        TechnologyDictionaryModel
      >(request, "/technologies");
      this.clearCache();
      return technology;
    } catch (error) {
      console.error("Error creating technology:", error);
      throw new Error(
        "Technology creation not implemented yet - backend endpoint missing"
      );
    }
  }

  /**
   * Update an existing technology in dictionary
   * PUT /Dictionary/technologies/{id}
   *
   * TODO: Backend endpoint not implemented yet
   */
  async updateTechnology(
    technologyId: number,
    request: TechnologyUpdateRequest
  ): Promise<TechnologyDictionaryModel> {
    try {
      // TODO: This will fail until backend endpoint is implemented
      const technology = await this.put<
        TechnologyUpdateRequest,
        TechnologyDictionaryModel
      >(request, `/technologies/${technologyId}`);
      this.clearCache();
      return technology;
    } catch (error) {
      console.error("Error updating technology:", error);
      throw new Error(
        "Technology update not implemented yet - backend endpoint missing"
      );
    }
  }

  /**
   * Delete a technology from dictionary
   * DELETE /Dictionary/technologies/{id}
   *
   * TODO: Backend endpoint not implemented yet
   */
  async deleteTechnology(technologyId: number): Promise<void> {
    try {
      // TODO: This will fail until backend endpoint is implemented
      await this.delete(`/technologies/${technologyId}`);
      this.clearCache();
    } catch (error) {
      console.error("Error deleting technology:", error);
      throw new Error(
        "Technology deletion not implemented yet - backend endpoint missing"
      );
    }
  }

  /**
   * Consolidate two technologies (merge source into target)
   * POST /Dictionary/technologies/consolidate
   */
  async consolidateTechnologies(
    request: ConsolidateTechnologiesRequest
  ): Promise<TechnologyDictionaryModel> {
    try {
      const technology = await this.post<
        ConsolidateTechnologiesRequest,
        TechnologyDictionaryModel
      >(request, "/technologies/consolidate");
      this.clearCache();
      return technology;
    } catch (error) {
      console.error("Error consolidating technologies:", error);
      throw error;
    }
  }

  /**
   * Helper method to get technology by ID from cache or API
   */
  async getTechnologyById(
    id: number
  ): Promise<TechnologyDictionaryModel | undefined> {
    const technologies = await this.getTechnologiesDictionary();
    return technologies.find((tech) => tech.id === id);
  }
}

// Create and export a singleton instance
export const technologyDictionaryAdminService =
  new TechnologyDictionaryAdminService();
